import React, { useState, ChangeEvent, FormEvent, useEffect } from "react";
import ToastComponent, {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "../../../components/ToastComponent";
import { Box, CircularProgress } from "@mui/material";

interface ForgotPasswordProps {
  onBack: () => void; // Callback to navigate back to the Login page
}
const vyu_api_url = process.env.REACT_APP_VYU_API_URL;

const ForgotPassword: React.FC<ForgotPasswordProps> = ({ onBack }) => {
  const [email, setEmail] = useState<string>("");
  const [step, setStep] = useState<number>(1); // Step 1: Enter Email, Step 2: Enter Code and New Password
  const [code, setCode] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [repassword, setRepassword] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false); // Loading state

  const handleEmailSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!email) {
      handleWarning("Please enter your email address.");
      return;
    }
    setLoading(true); // Start loading

    try {
      const response = await fetch(
        `${vyu_api_url}/forgot-password?email=${encodeURIComponent(email)}`,
        {
          method: "POST",
        }
      );
      console.log("Response inside forgot password: ", response);
      if (response.ok) {
        handleSuccess("Code sent to your email. Please check and proceed.");
        setStep(2); // Move to the next step
      } else {
        // Attempt to extract the error message
        const errorResponse = await response.json().catch(() => null);
        console.log("errorResponse: ", errorResponse);

        const errorMessage =
          errorResponse?.message || "Failed to send reset code.";
        handleError(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error during password reset request:", error);
      handleError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleResetSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!code || !password || !repassword) {
      handleWarning("Please fill in all the fields.");
      return;
    }

    if (password !== repassword) {
      handleWarning("Passwords do not match.");
      return;
    }
    setLoading(true); // Start loading
    try {
      const response = await fetch(`${vyu_api_url}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, token: code, password }),
      });

      if (response.ok) {
        handleSuccess("Password reset successful! You can now log in.");
        onBack(); // Navigate back to Login
      } else {
        const errorMessage = await response.text();
        handleError(`Error: ${errorMessage}`);
      }
    } catch (error) {
      console.error("Error during password reset request:", error);
      handleError("An unexpected error occurred. Please try again later.");
    } finally {
      setLoading(false); // Stop loading
    }
  };

  const handleWarning = (message: string) => {
    showWarningToast(message);
  };
  const handleSuccess = (message: string) => {
    showSuccessToast(message);
  };
  const handleError = (message: string) => {
    showErrorToast(message);
  };

  return (
    <div className="forgot-password-page">
      {step === 1 ? (
        <form onSubmit={handleEmailSubmit}>
          <div className="input-pair mb-3">
            <label htmlFor="email" className="form-label">
              Email
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your Email"
              required
            />
          </div>

          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
                height: "2%",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              Send Code
            </button>
          )}
        </form>
      ) : (
        <form onSubmit={handleResetSubmit}>
          <div className="input-pair mb-3">
            <label htmlFor="code" className="form-label">
              Code
            </label>
            <input
              type="text"
              className="form-control"
              id="code"
              name="code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="Enter the code"
              required
            />
          </div>
          <div className="input-pair mb-3">
            <label htmlFor="password" className="form-label">
              New Password
            </label>
            <input
              type="password"
              className="form-control"
              id="password_12345"
              name="password_12345"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              required
            />
          </div>
          <div className="input-pair mb-3">
            <label htmlFor="repassword" className="form-label">
              Re-Enter Password
            </label>
            <input
              type="password"
              className="form-control"
              id="repassword"
              name="repassword"
              value={repassword}
              onChange={(e) => setRepassword(e.target.value)}
              placeholder="Re-enter new password"
              required
            />
          </div>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignContent: "center",
                alignItems: "center",
                height: "2%",
                backgroundColor: "#FFFFFF",
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <button
              type="submit"
              className="btn btn-primary w-100"
              disabled={loading}
            >
              Reset Password
            </button>
          )}
        </form>
      )}
      <button onClick={onBack} className="btn btn-secondary mt-3 w-100">
        Back to Login
      </button>
    </div>
  );
};

export default ForgotPassword;
